import { useState, useEffect } from "react";
import useFetch from "../../hooks/useFetch";
import { create } from "../../services/upload-files.service";
import { useNavigate } from "react-router";
import CustomDatePicker from "./CustomDatePicker";

const FormTemplates = ({ selectedOption }) => {
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [resetDate, setResetDate] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useFetch(`templates/${selectedOption}`);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setFormData((prevData) => ({ ...prevData, [name]: fieldValue }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateFormFields(formData);
    if (Object.keys(errors).length === 0) {
      const convertedFormData = data.fields.map((field) => {
        const { id, field_name, field_label } = field;

        let field_text = formData[field_name] || "";
        
        // Format date fields before submission
        if (field.field_type === "date" && field_text) {
          const date = new Date(field_text);
          field_text = formatDateToDisplay(date); // Format as "04 Jun 2025"
        }

        const field_value = field.field_data
          ? field.field_data.find((option) => option.data === field_text)?.id
          : field_text;
        return {
          field_id: id,
          field_value,
          field_text,
        };
      });

      if (Object.keys(convertedFormData).length > 0) {
        create(convertedFormData, "templates/form").then((res) => {
          if (res && Object.keys(res).length > 0) {
            const route = "/goaldetailpage";
            navigate(route, { state: { id: res.id } });
          }
        });
      }
      setFormData({});
    } else {
      setValidationErrors(errors);
    }
  };

  // Helper function to format date as "04 Jun 2025"
  const formatDateToDisplay = (date) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleDateChange = (fieldName, date) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: date }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
  };

  const handleReset = () => {
    setFormData({});
    setValidationErrors({});
    setResetDate(true);
  };

  const validateFormFields = (formData) => {
    const errors = {};
    data?.fields?.forEach((field) => {
      const { field_name, field_label, field_validation, field_type } = field;
      const value = formData[field_name];
      if (
        field_validation &&
        (!value || (typeof value === "string" && value.trim() === ""))
      ) {
        errors[field_name] = `${field_label} is Required`;
      } else if (field_type === "email" && value && !isValidEmail(value)) {
        errors[field_name] = `${field_label} is Not a Valid Email`;
      } else if (field_type === "number" && value && !isValidNumber(value)) {
        errors[field_name] = `${field_label} is Not a Valid Number`;
      }
    });
    return errors;
  };

  const isValidEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const isValidNumber = (value) => {
    const numberRegex = /^\d+$/;
    return numberRegex.test(value);
  };

  useEffect(() => {
    setFormData({});
    setValidationErrors({});
  }, [selectedOption]);

  if (!selectedOption) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
            <h1 className="h2 py-2">
              <i className="fa-solid fa-spinner fa-spin"></i>
            </h1>
          </div>
        </main>
      ) : (
        <form className="creategoalform row" onSubmit={handleSubmit}>
          {data && data.fields && data.fields.length > 0 ? (
            data.fields.map((field) => (
              <div className="mt-3 creategoalfield" key={field.id}>
                <label className="form-label" htmlFor={field.field_name}>
                  {field.field_label} <span className="redstar">*</span>
                </label>
                {field.field_type === "dropdown" ? (
                  <select
                    className="form-select"
                    id={field.field_name}
                    name={field.field_name}
                    value={formData[field.field_name] || ""}
                    onChange={handleInputChange}
                  >
                    <option value="" disabled hidden>
                      Select an option
                    </option>
                    {field.field_data.map((option) => (
                      <option key={option.id} value={option.data}>
                        {option.data}
                      </option>
                    ))}
                  </select>
                ) : field.field_type === "checkbox" ? (
                  <div>
                    {field.field_data.map((option) => (
                      <div key={option.id}>
                        <input
                          className="form-control"
                          type="checkbox"
                          id={`${field.field_name}_${option.id}`}
                          name={field.field_name}
                          value={option.data}
                          checked={formData[field.field_name] === option.data}
                          onChange={handleInputChange}
                        />
                        <label
                          className="form-label"
                          htmlFor={`${field.field_name}_${option.id}`}
                        >
                          {option.data}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : field.field_type === "date" ? (
                  <div>
                    <CustomDatePicker
                      selected={formData[field.field_name]}
                      dateFormat="dd MMM yyyy"
                      showYearDropdown
                      dropdownMode="select"
                      resetDate={resetDate}
                      onDateChange={(date) =>
                        handleDateChange(field.field_name, date)
                      }
                      setResetDate={setResetDate}
                    />
                    {formData[field.field_name] && (
                      <div className="mt-2">
                        Selected: {formatDateToDisplay(formData[field.field_name])}
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    className="form-control"
                    type={field.field_type}
                    id={field.field_name}
                    name={field.field_name}
                    value={formData[field.field_name] || ""}
                    onChange={handleInputChange}
                  />
                )}
                {validationErrors[field.field_name] && (
                  <p className="error">{validationErrors[field.field_name]}</p>
                )}
              </div>
            ))
          ) : (
            <p className="mt-2">No Employee data available.</p>
          )}
          {data && data.fields && data.fields.length > 0 ? (
            <div className="mt-3 mb-3">
              <button className="btn btn-primary btn-add" type="submit">
                Submit
              </button>
              <button
                className="btn btn-light"
                type="button"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          ) : (
            ""
          )}
        </form>
      )}
    </>
  );
};
export default FormTemplates;